"use client";

import { useState } from "react";
import { Plus, Minus } from "lucide-react";

import { type FaqItem } from "@/components/seo/article-jsonld";

interface RevealFaqProps {
  items: readonly FaqItem[];
}

/**
 * Click-to-reveal FAQ accordion. Replaces the static h3+p list — same
 * data, but presented as collapsed cards the reader chooses to open.
 * Drives interaction (each open is a small commitment) and makes the
 * FAQ section less of a wall.
 *
 * Note: the FAQPage JSON-LD on the article still serializes all
 * questions/answers in plain HTML (search engines see them
 * unconditionally). Visual collapse is purely a UX layer.
 */
export function RevealFaq({ items }: RevealFaqProps) {
  const [openIdx, setOpenIdx] = useState<number | null>(0);

  return (
    <div className="my-8 divide-y divide-ink-200 overflow-hidden rounded-2xl bg-white ring-1 ring-ink-200">
      {items.map((item, idx) => {
        const open = openIdx === idx;
        return (
          <div key={item.question}>
            <button
              type="button"
              onClick={() => setOpenIdx(open ? null : idx)}
              aria-expanded={open}
              className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition hover:bg-cream-50/50 sm:px-6"
            >
              <span className="text-base font-semibold text-ink sm:text-lg">
                {item.question}
              </span>
              {open ? (
                <Minus
                  className="h-5 w-5 shrink-0 text-coral"
                  aria-hidden
                />
              ) : (
                <Plus className="h-5 w-5 shrink-0 text-ink-500" aria-hidden />
              )}
            </button>
            {open && (
              <div className="px-5 pb-5 text-base leading-relaxed text-ink-700 sm:px-6 sm:pb-6">
                {item.answer}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
